import type { AxiosPromise } from 'axios';
import BaseClient, { type ExtendedAxiosRequestConfig } from './BaseClient';
import type { CursorPaginationResponse } from './api';
import type {
    CreateRndFollowUpRunData,
    CreateRndRunData,
    RndAttributeKeyParams,
    RndBalance,
    RndRun,
    RndRunContact,
    RndRunContactsParams,
    RndRunDetails,
    RndRunsParams,
    RndSchedule,
    RndTeamConfigPayload,
    RndTeamConfigSaved,
    RndTeamDetails,
} from './adminRndClient.types';

class AdminRndClient extends BaseClient {
    getTeam(teamId: number, config?: ExtendedAxiosRequestConfig): AxiosPromise<RndTeamDetails> {
        return this.makeCoreApiRequest(`/api/admin/teams/${teamId}/rnd`, 'get', null, null, config);
    }

    updateTeam(teamId: number, config: RndTeamConfigPayload): AxiosPromise<RndTeamConfigSaved> {
        return this.makeCoreApiRequest(`/api/admin/teams/${teamId}/rnd`, 'put', config);
    }

    getAttributeKeys(
        teamId: number,
        params?: RndAttributeKeyParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<{ data: string[] }> {
        return this.makeCoreApiRequest(`/api/admin/teams/${teamId}/rnd/attribute-keys`, 'get', null, params, config);
    }

    getBalance(): AxiosPromise<RndBalance> {
        return this.makeCoreApiRequest('/api/admin/rnd/balance');
    }

    getRuns(
        params: RndRunsParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<CursorPaginationResponse<RndRun>> {
        return this.makeCoreApiRequest('/api/admin/rnd/runs', 'get', null, params, config);
    }

    getRun(runId: number, config?: ExtendedAxiosRequestConfig): AxiosPromise<RndRunDetails> {
        return this.makeCoreApiRequest(`/api/admin/rnd/runs/${runId}`, 'get', null, null, config);
    }

    getRunContacts(
        runId: number,
        params: RndRunContactsParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<Omit<CursorPaginationResponse<RndRunContact>, 'last_page_url' | 'prev_page_url'>> {
        return this.makeCoreApiRequest(`/api/admin/rnd/runs/${runId}/contacts`, 'get', null, params, config);
    }

    createRun(data: CreateRndRunData): AxiosPromise<RndRun> {
        return this.makeCoreApiRequest('/api/admin/rnd/runs', 'post', data);
    }

    cancelRun(runId: number): AxiosPromise<RndRun> {
        return this.makeCoreApiRequest(`/api/admin/rnd/runs/${runId}/cancel`, 'post');
    }

    createFollowUpRun(runId: number, data: CreateRndFollowUpRunData): AxiosPromise<RndRun> {
        return this.makeCoreApiRequest(`/api/admin/rnd/runs/${runId}/follow-up`, 'post', data);
    }

    approveRun(runId: number): AxiosPromise<RndRun> {
        return this.makeCoreApiRequest(`/api/admin/rnd/runs/${runId}/approve`, 'post');
    }

    denyRun(runId: number): AxiosPromise<RndRun> {
        return this.makeCoreApiRequest(`/api/admin/rnd/runs/${runId}/deny`, 'post');
    }

    getSchedules(config?: ExtendedAxiosRequestConfig): AxiosPromise<{ data: RndSchedule[] }> {
        return this.makeCoreApiRequest('/api/admin/rnd/schedules', 'get', null, null, config);
    }
}

export default new AdminRndClient();
